import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from '../api/client'
import type { EventInfo } from '../types'
import DebriefPanel from '../components/DebriefPanel'
import QuarterClock from '../components/QuarterClock'

type Tab = 'run' | 'debrief'

const titleCase = (s: string) =>
  s.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

function useFacilitatorTab(sessionId: string): [Tab, (t: Tab) => void] {
  const storageKey = `facilitator-tab:${sessionId}`
  const [tab, setTabState] = useState<Tab>(() => {
    if (typeof window === 'undefined') return 'run'
    const stored = window.localStorage.getItem(storageKey)
    return stored === 'debrief' ? 'debrief' : 'run'
  })
  const setTab = (t: Tab) => {
    setTabState(t)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, t)
    }
  }
  return [tab, setTab]
}

export default function FacilitatorView() {
  const { sessionId = '' } = useParams()
  const qc = useQueryClient()
  const [tab, setTab] = useFacilitatorTab(sessionId)

  const sessQ = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => api.getSession(sessionId),
    refetchInterval: 3_000,
  })
  const lbQ = useQuery({
    queryKey: ['leaderboard', sessionId],
    queryFn: () => api.getLeaderboard(sessionId),
    refetchInterval: 5_000,
  })
  const evQ = useQuery({
    queryKey: ['session-events', sessionId],
    queryFn: () => api.getSessionEvents(sessionId),
    refetchInterval: 5_000,
  })
  const eventCatQ = useQuery({
    queryKey: ['events-catalog'],
    queryFn: api.getEventsCatalog,
    staleTime: 60_000,
  })

  const startMut = useMutation({
    mutationFn: () => api.startSession(sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  })

  const advanceMut = useMutation({
    mutationFn: () => api.advanceQuarter(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session', sessionId] })
      qc.invalidateQueries({ queryKey: ['leaderboard', sessionId] })
      qc.invalidateQueries({ queryKey: ['session-events', sessionId] })
      qc.invalidateQueries({ queryKey: ['session-history', sessionId] })
    },
  })

  const finishMut = useMutation({
    mutationFn: () => api.finishSession(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session', sessionId] })
      qc.invalidateQueries({ queryKey: ['leaderboard', sessionId] })
    },
  })

  const pauseMut = useMutation({
    mutationFn: () => api.pauseSession(sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  })
  const resumeMut = useMutation({
    mutationFn: () => api.resumeSession(sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  })
  const adjustMut = useMutation({
    mutationFn: (deltaSeconds: number) => api.adjustTimer(sessionId, deltaSeconds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  })

  function handleAdvance() {
    const total = sessQ.data?.team_ids.length ?? 0
    const submitted = sessQ.data?.submitted_team_ids.length ?? 0
    const unsubmitted = total - submitted
    if (unsubmitted > 0) {
      const msg = `${unsubmitted} of ${total} team(s) haven't submitted. `
        + `Advance anyway? Their quarter will run with a no-op decision.`
      if (!window.confirm(msg)) return
    }
    advanceMut.mutate()
  }

  function handleFinishSession() {
    const remaining = (sessQ.data?.num_quarters ?? 0) - (sessQ.data?.current_quarter ?? 0)
    const msg = remaining > 0
      ? `Finish this session now? ${remaining} quarter(s) will be skipped. `
        + `Snapshots captured so far remain available in the debrief and Excel export. `
        + `This can't be undone.`
      : `Finish this session now? This can't be undone.`
    if (window.confirm(msg)) finishMut.mutate()
  }

  if (sessQ.isLoading) return <div className="p-8 text-slate-500">Loading session…</div>
  if (!sessQ.data)
    return <div className="p-8 text-danger">Session not found.</div>

  const s = sessQ.data
  const lb = lbQ.data
  const events = evQ.data ?? []
  const eventById = new Map((eventCatQ.data ?? []).map((e) => [e.id, e]))

  const joinUrl = `${window.location.origin}/session/${s.id}/join`

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto px-6 pt-3 pb-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-brand-700">Facilitator</h1>
              <p className="text-sm text-slate-500">
                {s.name} · {titleCase(s.scenario)} · {s.competitive_dynamics_mode ? 'Competitive' : 'Solo'}
                {' '}· Q{Math.min(s.current_quarter + 1, s.num_quarters)}/{s.num_quarters} · {titleCase(s.status)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <QuarterClock
                session={s}
                onPause={() => pauseMut.mutate()}
                onResume={() => resumeMut.mutate()}
                onAdjust={(delta) => adjustMut.mutate(delta)}
                pending={pauseMut.isPending || resumeMut.isPending || adjustMut.isPending}
              />
              {s.status === 'lobby' && (
                <button
                  className="btn-primary"
                  disabled={s.team_ids.length === 0 || startMut.isPending}
                  onClick={() => startMut.mutate()}
                >
                  {startMut.isPending ? 'Starting…' : 'Start Session'}
                </button>
              )}
              {s.status === 'active' && (
                <>
                  <button
                    type="button"
                    className="text-xs text-slate-500 hover:text-danger underline-offset-2 hover:underline mr-4"
                    disabled={finishMut.isPending}
                    onClick={handleFinishSession}
                  >
                    {finishMut.isPending ? 'Finishing…' : 'Finish Session'}
                  </button>
                  <button
                    className="btn-primary"
                    disabled={advanceMut.isPending || finishMut.isPending}
                    onClick={handleAdvance}
                  >
                    {advanceMut.isPending ? 'Completing…' : `Complete Q${s.current_quarter + 1}`}
                  </button>
                </>
              )}
              {s.status === 'complete' && (
                <a
                  className="btn-secondary"
                  href={`/api/sessions/${s.id}/export.xlsx`}
                  rel="noreferrer"
                >
                  Export Excel
                </a>
              )}
            </div>
          </div>

          <TabStrip tab={tab} setTab={setTab} />
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 py-6">
        {tab === 'run' ? (
          <div className="grid grid-cols-12 gap-6">
            <section className="col-span-12 lg:col-span-4 space-y-4">
              <div className="card">
                <h2 className="text-base font-semibold mb-2">Join URL</h2>
                <div className="flex items-stretch gap-2">
                  <code className="flex-1 text-xs break-all bg-slate-100 p-2 rounded font-mono">{joinUrl}</code>
                  <CopyButton value={joinUrl} />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Share this with teams or display the session code <code className="font-mono">{s.id}</code>.
                </p>
              </div>

              <div className="card">
                <h2 className="text-base font-semibold mb-2 flex items-baseline justify-between">
                  <span>Teams ({s.team_ids.length})</span>
                  {s.status === 'active' && s.team_ids.length > 0 && (
                    <span className="text-xs font-normal text-slate-500">
                      {s.submitted_team_ids.length}/{s.team_ids.length} submitted
                    </span>
                  )}
                </h2>
                {s.team_ids.length === 0 ? (
                  <p className="text-sm text-slate-500">Waiting for teams to join…</p>
                ) : (
                  <ul className="space-y-1">
                    {s.team_ids.map((tid) => {
                      const submitted = s.submitted_team_ids.includes(tid)
                      const isAI = s.ai_team_ids.includes(tid)
                      const isThinking = s.ai_thinking_team_ids.includes(tid)
                      return (
                        <li key={tid} className="flex items-center justify-between text-sm gap-2">
                          <span className="font-medium truncate">
                            {isAI && <span aria-hidden className="mr-1">🤖</span>}
                            {s.team_names[tid]}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            {s.status === 'active' && (
                              isThinking ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium animate-pulse">
                                  Thinking…
                                </span>
                              ) : submitted ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                                  Submitted
                                </span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                                  Waiting
                                </span>
                              )
                            )}
                            <span className="text-xs text-slate-500 font-mono">{tid}</span>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </section>

            <section className="col-span-12 lg:col-span-4">
              <div className="card">
                <h2 className="text-base font-semibold mb-3">Leaderboard</h2>
                {!lb || lb.entries.length === 0 ? (
                  <p className="text-sm text-slate-500">No scores yet.</p>
                ) : (
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-8" />
                      <col />
                      <col className="w-16" />
                      <col className="w-16" />
                      <col className="w-14" />
                      <col className="w-10" />
                    </colgroup>
                    <thead>
                      <tr className="text-left text-xs text-slate-500 uppercase">
                        <th className="pb-2">#</th>
                        <th className="pb-2">Team</th>
                        <th className="pb-2 text-right">Composite</th>
                        <th className="pb-2 text-right">Value</th>
                        <th className="pb-2 text-right">Adopt</th>
                        <th className="pb-2 text-right">Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lb.entries.map((e, i) => (
                        <tr key={e.team_id} className="border-t border-slate-100">
                          <td className="py-2">{i + 1}</td>
                          <td className="py-2 font-medium">
                            <div className="relative group">
                              <div className="truncate">{e.team_name}</div>
                              <div className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block">
                                {e.team_name}
                              </div>
                            </div>
                          </td>
                          <td className="py-2 text-right">{e.composite.toFixed(1)}</td>
                          <td className="py-2 text-right">${e.value_est_m.toFixed(1)}M</td>
                          <td className="py-2 text-right">{e.adoption_pct.toFixed(0)}%</td>
                          <td className="py-2 text-right">L{e.max_risk_alert}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            <section className="col-span-12 lg:col-span-4">
              <div className="card">
                <EventHistoryList
                  events={events}
                  teamNames={s.team_names}
                  eventById={eventById}
                />
              </div>
            </section>
          </div>
        ) : (
          <DebriefPanel sessionId={s.id} />
        )}
      </main>
    </div>
  )
}

interface EventRow { quarter: number; team_id: string; event_id: string }

interface SessionEventGroup {
  quarter: number
  team_id: string
  events: string[]
}

function EventHistoryList({
  events, teamNames, eventById,
}: {
  events: SessionEventGroup[]
  teamNames: Record<string, string>
  eventById: Map<string, EventInfo>
}) {
  // Flatten groups into one row per fired event, newest first.
  const rows: EventRow[] = []
  for (const g of events) {
    for (const eid of g.events) {
      rows.push({ quarter: g.quarter, team_id: g.team_id, event_id: eid })
    }
  }
  rows.reverse()

  return (
    <>
      <h2 className="text-base font-semibold mb-3">
        Event History ({rows.length})
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No events fired yet.</p>
      ) : (
        <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
          {rows.map((r, i) => (
            <EventHistoryRow
              key={`${r.quarter}-${r.team_id}-${r.event_id}-${i}`}
              row={r}
              teamName={teamNames[r.team_id] ?? r.team_id}
              info={eventById.get(r.event_id)}
            />
          ))}
        </div>
      )}
    </>
  )
}

function EventHistoryRow({
  row, teamName, info,
}: {
  row: EventRow
  teamName: string
  info: EventInfo | undefined
}) {
  const [expanded, setExpanded] = useState(false)
  const name = info?.name ?? row.event_id
  const narrative = info?.narrative
  const hint = info?.response_hint
  const hasDetail = Boolean(narrative || hint)

  return (
    <div className="border-l-2 border-amber-400 pl-2 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-xs font-mono text-slate-500 truncate">
          Q{row.quarter} · {teamName} · {row.event_id}
        </div>
        {hasDetail && (
          <button
            type="button"
            className="text-xs text-brand-700 hover:underline shrink-0"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Less' : 'More'}
          </button>
        )}
      </div>
      <div className="font-medium text-slate-900">{name}</div>
      {narrative && (
        <p className={`text-xs text-slate-600 ${expanded ? '' : 'line-clamp-2'}`}>
          {narrative}
        </p>
      )}
      {expanded && hint && (
        <p className="text-xs text-slate-500 italic mt-1">
          <span className="font-semibold not-italic">Coaching:</span> {hint}
        </p>
      )}
    </div>
  )
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Fallback for non-secure contexts where the Clipboard API is blocked.
      const ta = document.createElement('textarea')
      ta.value = value
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch { /* ignore */ }
      document.body.removeChild(ta)
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Copy join URL to clipboard"
      className="shrink-0 self-stretch px-3 rounded text-xs font-medium bg-slate-200 hover:bg-slate-300 text-slate-700 transition-colors"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  )
}

function TabStrip({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  return (
    <div className="flex gap-1 mt-3 -mb-px" role="tablist">
      <TabButton active={tab === 'run'} onClick={() => setTab('run')}>
        Run
      </TabButton>
      <TabButton active={tab === 'debrief'} onClick={() => setTab('debrief')}>
        Debrief
      </TabButton>
    </div>
  )
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        'px-4 py-2 text-sm font-medium border-b-2 transition-colors ' +
        (active
          ? 'border-brand-600 text-brand-700'
          : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300')
      }
    >
      {children}
    </button>
  )
}
